import { organizationSubscriptionPlanTKeys } from '@modules-deprecated/app/organization/enums/organizationSubscriptionPlanTKeys'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { subscriptionPlansCatalog } from '../constants/subscriptionPlansCatalog'
import { SubscriptionPlanBehavior } from '../types/subscriptionPlanBehavior'
import { SubscriptionPlanContent } from '../types/subscriptionPlanContent'

type ResolvedSubscriptionPlan = {
  plan: SubscriptionPlan
  displayAs?: SubscriptionPlan
  behavior: SubscriptionPlanBehavior | undefined
  content: SubscriptionPlanContent
}

/**
 * Resolves what a plan should actually behave/look like, applying overrides in this
 * precedence order (highest wins):
 *
 * 1. Market `displayAs` (`marketOverrides[market].displayAs`) — for this market, the plan
 *    is entirely re-pointed at another plan; behavior/content are inherited from resolving
 *    that other plan instead (chased recursively, in case that plan is itself aliased).
 * 2. Market `behavior`/`content` patch — for this market, the plan keeps its own identity
 *    but has its behavior/content shallow-merged with market-specific values. This also
 *    cancels any default-level aliasing (step 3) for that market: a market patch always
 *    means "use (patched) values for this plan", never "look at the aliased plan".
 * 3. Default `displayAs` (`entry.displayAs`) — with no market override in play, the plan
 *    has no behavior/content of its own and instead inherits them from another plan
 *    (again resolved recursively).
 * 4. Default `behavior`/`content` (`entry.behavior`/`entry.content`) — used as-is when none
 *    of the above apply.
 * 5. Fallback — plan missing from the catalog, or resolved with no content — falls back to
 *    just the translated plan name.
 *
 * Note the returned `plan` is always the originally requested plan; `displayAs` is the final
 * plan at the end of the alias chain (used by callers to decide what to actually render/pick).
 */
export const resolveSubscriptionPlan = (
  plan: SubscriptionPlan,
  market?: 'de' | 'fr' | 'nl',
): ResolvedSubscriptionPlan => {
  const entry = subscriptionPlansCatalog[plan]

  if (!entry) {
    return { plan, behavior: undefined, content: { name: organizationSubscriptionPlanTKeys[plan] ?? '' } }
  }

  const marketOverride = market ? entry.marketOverrides?.[market] : undefined

  // Start from the default (non-market) layer: if the plan aliases another plan by default,
  // it has no behavior/content of its own yet (step 3/4 above).
  let effectiveDisplayAs = entry.displayAs
  let effectiveBehavior = entry.displayAs ? undefined : entry.behavior
  let effectiveContent = entry.displayAs ? undefined : entry.content

  if (marketOverride) {
    if (marketOverride.displayAs) {
      // Market re-points the plan at another plan entirely (step 1): drop any default
      // behavior/content, they'll come from resolving that other plan below.
      effectiveDisplayAs = marketOverride.displayAs
      effectiveBehavior = undefined
      effectiveContent = undefined
    } else {
      // Market provides its own behavior/content (step 2): the plan keeps its own identity
      // for this market, so any default-level aliasing is cancelled, and the market values
      // are merged on top of whatever default behavior/content this plan had (if any).
      effectiveDisplayAs = undefined
      effectiveBehavior = { ...effectiveBehavior, ...marketOverride.behavior } as SubscriptionPlanBehavior
      effectiveContent = { ...effectiveContent, ...marketOverride.content } as SubscriptionPlanContent
    }
  }

  if (effectiveDisplayAs) {
    // Follow the alias (default- or market-level) to inherit its behavior/content, chasing
    // through if that plan is itself aliased, while keeping track of the final target plan.
    const aliasedResult = resolveSubscriptionPlan(effectiveDisplayAs, market)
    const finalDisplayAs = aliasedResult.displayAs ?? effectiveDisplayAs
    return { plan, displayAs: finalDisplayAs, behavior: aliasedResult.behavior, content: aliasedResult.content }
  }

  return {
    plan,
    behavior: effectiveBehavior,
    content: effectiveContent ?? { name: organizationSubscriptionPlanTKeys[plan] ?? '' },
  }
}
